import httpClient from '~/lib/http-client';

// ─── Static Deployments (Landing Pages) ───

export type StaticDeployStep = 'building' | 'uploading' | 'provisioning-db' | 'migrating' | 'deploying-worker' | null;

export interface StaticDeployment {
  id: string;
  chatId: string;
  userId: string;
  subdomain: string;
  customDomain: string | null;
  customHostnameId: string | null;
  r2Prefix: string;
  /**
   * "Made with Dunea" badge on the published site. Free plans always ship the
   * badge (server-enforced); paid plans may turn it off. Applies on deploy.
   */
  showBadge: boolean;
  deployUrl: string;
  status: 'deploying' | 'live' | 'failed' | 'deleted';
  deployStep: StaticDeployStep;
  errorMessage: string | null;
  deployedAt: string | null;
  createdAt: string;
  updatedAt: string;
  d1DatabaseId: string | null;
  workerName: string | null;
  /** Set while the site is offline because the owner is on the free plan. */
  pausedAt: string | null;
  /** Pre-publish plugin warnings (e.g. checkout still in test mode). Only on deploy/redeploy responses. */
  checkWarnings?: string[];
}

export async function apiStaticDeploy(
  chatId: string,
  subdomain?: string,
  showBadge?: boolean,
): Promise<StaticDeployment> {
  const { data } = await httpClient.post<StaticDeployment>('/api/deployments/static/deploy', {
    chatId,
    subdomain,
    showBadge,
  });
  return data;
}

/** Toggle the "Made with Dunea" badge. Hiding it is paid-only (403 PLAN_REQUIRED otherwise). */
export async function apiSetStaticBadge(id: string, show: boolean): Promise<StaticDeployment> {
  const { data } = await httpClient.post<StaticDeployment>(`/api/deployments/static/${id}/badge`, { show });
  return data;
}

export async function apiGetStaticDeployment(id: string): Promise<StaticDeployment> {
  const { data } = await httpClient.get<StaticDeployment>(`/api/deployments/static/${id}`);
  return data;
}

export async function apiGetStaticDeploymentByChatId(chatId: string): Promise<StaticDeployment | null> {
  try {
    const { data } = await httpClient.get<StaticDeployment>(`/api/deployments/static/by-chat/${chatId}`);
    return data;
  } catch {
    return null;
  }
}

export async function apiStaticRedeploy(id: string): Promise<StaticDeployment> {
  const { data } = await httpClient.post<StaticDeployment>(`/api/deployments/static/${id}/redeploy`);
  return data;
}

/**
 * DORMANT since 2026-09-04: every paid plan publishes for free, so no surface
 * calls this. Kept for the paused $1 charge — see `deploy-eligibility.ts`.
 *
 * Start the one-time $1 "publish unlock" checkout for a chat (Starter plan's
 * pay-to-publish); returns the hosted checkout URL. Once paid, the chat's first
 * deploy AND every redeploy are free. Server enforces Starter-only + not-already-
 * unlocked, so a 400 means "already unlocked / not applicable".
 */
export async function apiStartPublishUnlockCheckout(chatId: string): Promise<{ url: string }> {
  const { data } = await httpClient.post<{ url: string }>('/api/credits/publish-unlock/checkout', { chatId });
  return data;
}

export async function apiDeleteStaticDeployment(id: string): Promise<void> {
  await httpClient.delete(`/api/deployments/static/${id}`);
}

export async function apiChangeStaticSubdomain(id: string, subdomain: string): Promise<StaticDeployment> {
  const { data } = await httpClient.post<StaticDeployment>(`/api/deployments/static/${id}/subdomain`, { subdomain });
  return data;
}

export async function apiConnectStaticDomain(id: string, domain: string): Promise<StaticDeployment> {
  const { data } = await httpClient.post<StaticDeployment>(`/api/deployments/static/${id}/domain`, { domain });
  return data;
}

export async function apiDisconnectStaticDomain(id: string): Promise<StaticDeployment> {
  const { data } = await httpClient.delete<StaticDeployment>(`/api/deployments/static/${id}/domain`);
  return data;
}

export interface CustomDomainStatus {
  customDomain: string | null;
  verificationStatus: string;
  verified: boolean;
}

export async function apiGetStaticDomainStatus(id: string): Promise<CustomDomainStatus> {
  const { data } = await httpClient.get<CustomDomainStatus>(`/api/deployments/static/${id}/domain/status`);
  return data;
}

// ─── Form Submissions ───

export interface FormSubmission {
  id: string;
  siteId: string;
  formId: string;
  data: Record<string, unknown>;
  createdAt: string;
}

export interface FormSubmissionsResponse {
  submissions: FormSubmission[];
  total: number;
  limit: number;
  skip: number;
}

export async function apiGetFormSubmissions(
  deploymentId: string,
  options?: { formId?: string; limit?: number; skip?: number },
): Promise<FormSubmissionsResponse> {
  const params = new URLSearchParams();

  if (options?.formId) {
    params.set('formId', options.formId);
  }

  if (options?.limit) {
    params.set('limit', String(options.limit));
  }

  if (options?.skip) {
    params.set('skip', String(options.skip));
  }

  const { data } = await httpClient.get<FormSubmissionsResponse>(
    `/api/deployments/static/${deploymentId}/forms?${params.toString()}`,
  );

  return data;
}

export async function apiDeleteFormSubmission(deploymentId: string, submissionId: string): Promise<void> {
  await httpClient.delete(`/api/deployments/static/${deploymentId}/forms/${submissionId}`);
}

export async function apiExportFormSubmissions(
  deploymentId: string,
  formId?: string,
): Promise<{ csv: string; filename: string }> {
  const params = formId ? `?formId=${encodeURIComponent(formId)}` : '';
  const { data } = await httpClient.get<{ csv: string; filename: string }>(
    `/api/deployments/static/${deploymentId}/forms/export${params}`,
  );

  return data;
}

export async function apiGetFormIds(deploymentId: string): Promise<string[]> {
  const { data } = await httpClient.get<{ formIds: string[] }>(`/api/deployments/static/${deploymentId}/forms/ids`);
  return data.formIds;
}

export interface FormSettings {
  emailNotifications?: boolean;
  notifyEmails?: string[];
  webhookUrl?: string;
  redirectUrl?: string;
  successMessage?: string;
}

export async function apiGetFormSettings(deploymentId: string, formId: string): Promise<FormSettings | null> {
  const { data } = await httpClient.get<{ settings: FormSettings | null }>(
    `/api/deployments/static/${deploymentId}/forms/${formId}/settings`,
  );
  return data.settings;
}

export async function apiSaveFormSettings(deploymentId: string, formId: string, settings: FormSettings): Promise<void> {
  await httpClient.post(`/api/deployments/static/${deploymentId}/forms/${formId}/settings`, settings);
}
